import { supabase } from "./supabase";

// O IO do Legaliza Fácil (000115).
//
// Este módulo transporta. Quem decide se o aluno pode ler é a RLS
// (`conteudo_liberado('legaliza-facil') or eh_admin()`, nas duas tabelas e na
// policy do bucket); quem decide o que aparece na tela é `LegalizaFacil.tsx`.
// Aqui não há regra de acesso nem regra de apresentação.
//
// ---------------------------------------------------------------------------
// A união dos modelos mora AQUI, num lugar só
// ---------------------------------------------------------------------------
// Um estado mostra três origens de documento: os modelos da própria junta, os
// genéricos da casa e os manuais do DREI. No banco isso é uma coluna `uf` que
// aceita nulo (nulo = vale para todos), e a junção é `modelosDoEstado`.
//
// No app de origem essa união era um `forEach` que MUTAVA o array de estados no
// carregamento do módulo, e ela misturava tudo numa lista só, agrupada por
// categoria, com um selo "CF" de 9px separando as origens. Em Goiás o resultado
// eram duas linhas vizinhas chamadas "Abertura Empresário Individual" e
// "Abertura - Empresário Individual CF". Aqui as origens continuam separadas até
// a tela, que as apresenta em blocos com a procedência no título.

export type Integrador = "rede_simples" | "regin" | "empresa_facil" | "vre";
export type CategoriaDeModelo =
  | "abertura"
  | "alteracao"
  | "baixa"
  | "transformacao"
  | "manual";
export type OrigemDeModelo = "junta" | "cf" | "drei";

export type EstadoDaLegalizacao = {
  uf: string;
  nome: string;
  junta: string;
  integrador: Integrador;
  link_abertura: string;
  link_alteracao: string;
  link_baixa: string;
  nota_de_origem: string | null;
  links_conferidos_em: string | null;
  links_conferidos_por: string | null;
};

export type ModeloDeContrato = {
  id: string;
  uf: string | null;
  titulo: string;
  categoria: CategoriaDeModelo;
  origem: OrigemDeModelo;
  arquivo: string;
  formato: "docx" | "pdf";
  ordem: number;
  ativo: boolean;
};

export type Catalogo = {
  estados: EstadoDaLegalizacao[];
  modelos: ModeloDeContrato[];
};

export const INTEGRADORES: { valor: Integrador; rotulo: string; explica: string }[] = [
  {
    valor: "rede_simples",
    rotulo: "Rede Simples",
    explica: "Portal único da junta, com login pelo gov.br.",
  },
  {
    valor: "regin",
    rotulo: "REGIN",
    explica: "Requerimento Universal na junta; alteração e baixa pelo coletor da Receita.",
  },
  {
    valor: "empresa_facil",
    rotulo: "Empresa Fácil",
    explica: "Portal estadual próprio, um endereço por ato.",
  },
  { valor: "vre", rotulo: "VRE", explica: "Via Rápida Empresa — hoje só São Paulo." },
];

export const CATEGORIAS: { valor: CategoriaDeModelo; rotulo: string }[] = [
  { valor: "abertura", rotulo: "Abertura" },
  { valor: "alteracao", rotulo: "Alteração" },
  { valor: "baixa", rotulo: "Baixa" },
  { valor: "transformacao", rotulo: "Transformação" },
  { valor: "manual", rotulo: "Manual" },
];

export function rotuloDoIntegrador(i: Integrador): string {
  return INTEGRADORES.find((x) => x.valor === i)?.rotulo ?? i;
}

export function rotuloDaCategoria(c: CategoriaDeModelo): string {
  return CATEGORIAS.find((x) => x.valor === c)?.rotulo ?? c;
}

/**
 * Os avisos que aparecem em toda tela de estado.
 *
 * Constante, e não tabela: o corte da 000108 pergunta QUEM revê o valor, e este
 * texto é redação jurídica da casa — muda com revisão do jurídico, que é deploy
 * de todo modo. Salário mínimo e links de junta mudam por ato de terceiro e por
 * isso estão no banco; um parágrafo de responsabilidade, não.
 */
export const AVISOS = [
  "Os modelos de contrato aqui disponibilizados são básicos, podendo ser alteradas ou acrescentadas as cláusulas de acordo com a necessidade específica de cada situação.",
  "Em casos de registro de empresa junto a órgãos regulamentadores, é fundamental verificar previamente as exigências aplicáveis ao contrato social antes de realizar o registro na Junta Comercial.",
];

export const RESPONSABILIDADE =
  "Os modelos disponibilizados são de inteira responsabilidade de quem elabora o contrato, não sendo de responsabilidade da Contabilidade Facilitada. São documentos de apoio, para usar como referência.";

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

/**
 * O catálogo inteiro numa ida só.
 *
 * São 27 estados e 68 modelos — 95 linhas pequenas. Buscar por estado exigiria
 * uma ida ao servidor a cada clique no mapa, e o mapa é feito para ser
 * percorrido. É a mesma decisão de `lib/ocupacoes.ts`, que traz as 502 de uma vez
 * e filtra localmente.
 */
export async function lerCatalogo(): Promise<Catalogo | null> {
  const [estados, modelos] = await Promise.all([
    supabase
      .from("legaliza_estados")
      .select(
        "uf,nome,junta,integrador,link_abertura,link_alteracao,link_baixa,nota_de_origem,links_conferidos_em,links_conferidos_por",
      )
      .order("nome"),
    supabase
      .from("legaliza_modelos")
      .select("id,uf,titulo,categoria,origem,arquivo,formato,ordem,ativo")
      .eq("ativo", true)
      .order("ordem"),
  ]);
  if (estados.error || modelos.error) return null;
  return {
    estados: (estados.data ?? []) as EstadoDaLegalizacao[],
    modelos: (modelos.data ?? []) as ModeloDeContrato[],
  };
}

/** Os modelos que a JUNTA daquele estado publica. */
export function modelosDaJunta(catalogo: Catalogo, uf: string): ModeloDeContrato[] {
  return catalogo.modelos.filter((m) => m.uf === uf);
}

/** Os modelos genéricos da casa — os mesmos para os 27. */
export function modelosDaCasa(catalogo: Catalogo): ModeloDeContrato[] {
  return catalogo.modelos.filter((m) => m.origem === "cf");
}

/** Os manuais oficiais do DREI — valem para qualquer estado. */
export function manuaisDoDrei(catalogo: Catalogo): ModeloDeContrato[] {
  return catalogo.modelos.filter((m) => m.origem === "drei");
}

/** Um modelo pelo id — o que a rota do redator recebe. */
export function acharModelo(catalogo: Catalogo, id: string): ModeloDeContrato | undefined {
  return catalogo.modelos.find((m) => m.id === id);
}

/** Agrupa por categoria, na ordem da casa, pulando as vazias. */
export function porCategoria(
  modelos: ModeloDeContrato[],
): { categoria: CategoriaDeModelo; rotulo: string; itens: ModeloDeContrato[] }[] {
  return CATEGORIAS.map((c) => ({
    categoria: c.valor,
    rotulo: c.rotulo,
    itens: modelos.filter((m) => m.categoria === c.valor),
  })).filter((g) => g.itens.length > 0);
}

// ---------------------------------------------------------------------------
// Os arquivos
// ---------------------------------------------------------------------------

/**
 * Baixa o arquivo do bucket privado.
 *
 * `download` e não url pública: o bucket é privado e a policy pergunta pelo card
 * (`conteudo_liberado('legaliza-facil') or eh_admin()`). Um `getPublicUrl` aqui
 * devolveria um endereço que responde 400 — e o modo de falha seria um botão de
 * baixar que não baixa, sem mensagem.
 */
export async function baixarArquivo(caminho: string): Promise<Blob | null> {
  const { data, error } = await supabase.storage.from("legaliza").download(caminho);
  if (error || !data) return null;
  return data;
}

/**
 * Entrega o arquivo ao aluno com o nome certo.
 *
 * O nome do arquivo no bucket carrega a pasta do estado (`GO/modelo-….docx`), e
 * é isso que a constraint exige — mas o aluno não quer uma pasta no nome do
 * download. O nome sai do TÍTULO do modelo, que é o que ele acabou de ler na
 * tela.
 */
export function nomeParaDownload(titulo: string, formato: string, uf?: string | null): string {
  // A remoção de acento compara CODE POINT, e não casa um intervalo de regex.
  //
  // A primeira versão trazia `[U+0300-U+036F]` com os caracteres combinantes
  // literais dentro da classe — invisíveis no editor, e some em qualquer
  // passagem por shell. É a terceira vez que um escape se perde ao atravessar
  // duas camadas de string nesta casa, e a lição registrada é a mesma: quando dá
  // para escrever a forma que não tem escape, ela é melhor que o escape certo.
  const semAcento = [...titulo.normalize("NFD")]
    .filter((c) => {
      const n = c.codePointAt(0) ?? 0;
      return n < 0x300 || n > 0x36f;
    })
    .join("");
  const limpo = semAcento
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return `${uf ? uf.toLowerCase() + "-" : ""}${limpo}.${formato}`;
}


// ---------------------------------------------------------------------------
// Administração
// ---------------------------------------------------------------------------

/**
 * Salva os campos comuns de um estado. Links NÃO passam por aqui.
 *
 * A separação é deliberada: mexer num link é um ato com consequência (34 mil
 * contadores clicam nele), e ele tem carimbo de conferência. Trocar o nome da
 * junta não tem.
 */
export async function salvarEstado(
  uf: string,
  campos: { nome: string; junta: string; integrador: Integrador; nota_de_origem: string | null },
): Promise<string | null> {
  const { error } = await supabase.from("legaliza_estados").update(campos).eq("uf", uf);
  return error ? error.message : null;
}

/**
 * Grava os links e carimba a conferência — os dois no mesmo ato.
 *
 * Por que RPC e não `update`: o carimbo é do SERVIDOR. O relógio do navegador do
 * operador pode estar em qualquer data, e "quem conferiu" não pode ser um id que
 * veio no payload. É a decisão do `calculado_em` da 000071.
 *
 * E o inverso também está no banco: um `update` direto que MUDE um link zera o
 * carimbo, por gatilho. Conferência descreve os endereços que alguém viu, e morre
 * com eles.
 */
export async function confirmarLinks(
  uf: string,
  links: { abertura: string; alteracao: string; baixa: string },
): Promise<string | null> {
  const { error } = await supabase.rpc("legaliza_confirmar_links", {
    p_uf: uf,
    p_abertura: links.abertura,
    p_alteracao: links.alteracao,
    p_baixa: links.baixa,
  });
  return error ? error.message : null;
}

/** Liga ou desliga um modelo da lista do aluno, sem apagar o arquivo. */
export async function definirModeloAtivo(id: string, ativo: boolean): Promise<string | null> {
  const { error } = await supabase.from("legaliza_modelos").update({ ativo }).eq("id", id);
  return error ? error.message : null;
}

/** Corrige o título de um modelo — o que o aluno lê na lista. */
export async function renomearModelo(id: string, titulo: string): Promise<string | null> {
  const { error } = await supabase.from("legaliza_modelos").update({ titulo }).eq("id", id);
  return error ? error.message : null;
}

export type EstadoParaAdmin = EstadoDaLegalizacao & {
  /** Quem conferiu, pelo nome — embutido pela FK `links_conferidos_por → profiles`. */
  conferidor: { nome: string | null } | null;
};

/**
 * Os estados com o NOME de quem conferiu, para a tela de admin.
 *
 * O aluno não precisa disso (e a leitura dele não traz), mas a tela cujo botão
 * afirma "alguém abriu o site da junta e conferiu" tem de dizer QUEM — é a
 * lacuna que a tela de admin do Calendário tem e que a 000115 fechou aqui com
 * duas colunas. O embed passa pela RLS de `profiles`, e quem não puder ler o
 * perfil do conferidor recebe nulo — a tela diz "equipe" e segue.
 */
export async function lerEstadosParaAdmin(): Promise<EstadoParaAdmin[] | null> {
  const { data, error } = await supabase
    .from("legaliza_estados")
    .select(
      "uf,nome,junta,integrador,link_abertura,link_alteracao,link_baixa,nota_de_origem,links_conferidos_em,links_conferidos_por,conferidor:profiles!links_conferidos_por(nome)",
    )
    .order("nome");
  if (error) return null;
  return (data ?? []) as unknown as EstadoParaAdmin[];
}

/**
 * Os modelos INATIVOS também, que a tela de admin precisa ver para reativar.
 *
 * `lerCatalogo` filtra `ativo` porque é a leitura do aluno. Uma tela de
 * administração que só enxerga o que está ligado não consegue religar nada — é a
 * lição da 000090 pelo avesso: gate de escrita sem o de leitura correspondente.
 */
export async function lerModelosParaAdmin(): Promise<ModeloDeContrato[] | null> {
  const { data, error } = await supabase
    .from("legaliza_modelos")
    .select("id,uf,titulo,categoria,origem,arquivo,formato,ordem,ativo")
    .order("uf", { nullsFirst: true })
    .order("ordem");
  if (error) return null;
  return (data ?? []) as ModeloDeContrato[];
}
